import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createRoot } from 'react-dom/client';
import { useParams, useNavigate } from 'react-router-dom';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import {
  Box,
  IconButton,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  MyLocation,
  ZoomIn,
  ZoomOut,
  EvStation,
  Fullscreen,
} from '@mui/icons-material';
import { organizationService } from '../services/api';
import { GPSData } from '../types';
import { theme } from '../theme';
import { useOrgNavigation } from '../utils/navigation';
import { useSidebar } from '../contexts/SidebarContext';


// Mapbox access token - should be stored in environment variable
const MAPBOX_ACCESS_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

// Validate the access token format
const isValidMapboxToken = (token: string | undefined) => {
  return token && token.startsWith('pk.') && token.length > 50;
};

// Set access token only if valid
if (MAPBOX_ACCESS_TOKEN && isValidMapboxToken(MAPBOX_ACCESS_TOKEN)) {
  mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;
} else {
  console.warn('Invalid or missing Mapbox access token. Map functionality will be limited.');
}


// Custom marker component for chargers
const EvStationMarker: React.FC = () => {
  return (
    <div
      style={{
        width: '40px',
        height: '40px',
        backgroundColor: theme.palette.secondary.dark,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
        cursor: 'pointer',
      }}
    >
      <EvStation style={{ fontSize: '24px' }} />
    </div>
  );
};

// Custom marker component for machines
const MachineMarker: React.FC = () => {
  return (
    <div
      style={{
        width: '40px',
        height: '40px',
        backgroundColor: theme.palette.secondary.dark,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
        cursor: 'pointer',
      }}
    >
      <img 
        src={process.env.PUBLIC_URL + "/DiggerIcon.svg"} 
        alt="Machine" 
        style={{ 
          width: '24px', 
          height: '24px',
          filter: 'brightness(0) invert(1)' // Makes the icon white
        }}
      />
    </div>
  );
};

const Map: React.FC = () => {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const { navigateToOrgPath } = useOrgNavigation();
  const { sidebarCollapsed } = useSidebar();
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const markers = useRef<mapboxgl.Marker[]>([]);
  const [mapError, setMapError] = useState<string | null>(null);
  const [hasValidToken] = useState(isValidMapboxToken(MAPBOX_ACCESS_TOKEN));
  const [gpsData, setGpsData] = useState<(GPSData & { assetType?: 'charger' | 'machine' })[]>([]);
  const [loadingGPS, setLoadingGPS] = useState(false);
  const [gpsError, setGpsError] = useState<string | null>(null);
  const gpsDataFetched = useRef(false);
  const iconsPreloaded = useRef(false);
  const sourceId = 'gps-points';
  const clusterLayerId = 'gps-clusters';
  const clusterCountLayerId = 'gps-cluster-count';
  const eventHandlers = useRef<{
    clusterClick?: (e: mapboxgl.MapLayerMouseEvent) => void;
    clusterMouseEnter?: () => void;
    clusterMouseLeave?: () => void;
    mapMoveEnd?: () => void;
  }>({});
  // Default map center and zoom (middle of Norway)
  const lng = 12.1828;
  const lat = 63.5926;
  const zoom = 4;
  
  const sidebarWidth = sidebarCollapsed ? 64 : 240;

  const mapControls = [
    { icon: <ZoomIn />, action: 'zoomIn' },
    { icon: <ZoomOut />, action: 'zoomOut' },
    { icon: <MyLocation />, action: 'location' },
    { icon: <Fullscreen />, action: 'fullscreen' },
  ];

  // Function to handle navigation to charger detail
  const handleChargerNavigation = useCallback((unitId: string) => {
    const chargerPath = `/assets/chargers/${unitId}`;
    const orgPath = navigateToOrgPath(chargerPath);
    navigate(orgPath);
  }, [navigate, navigateToOrgPath]);

  // Function to handle navigation to machine detail
  const handleMachineNavigation = useCallback((unitId: string) => {
    const machinePath = `/assets/excavators/${unitId}/dashboard`;
    const orgPath = navigateToOrgPath(machinePath);
    navigate(orgPath);
  }, [navigate, navigateToOrgPath]);

  // Function to fetch GPS data for organization (chargers and machines)
  const fetchGPSData = useCallback(async () => {
    if (!orgId) {
      console.log('No orgId available for GPS fetch');
      return;
    }

    // Prevent duplicate calls if already fetched
    if (gpsDataFetched.current) {
      console.log('GPS data already fetched, skipping fetch');
      return;
    }
    
    setLoadingGPS(true);
    setGpsError(null);
    
    try {
      console.log('Fetching GPS data for organization:', orgId);
      
      const allGpsData: (GPSData & { assetType?: 'charger' | 'machine' })[] = [];
      
      const response = await organizationService.getOrganizationGPS(orgId);
      
      if (response.success && response.data) {
        console.log('Organization GPS API response:', response);
        
        // Only plot assets that belong to the organization.
        // GPS rows with no unit name are not assets (they show up as "Unit 1").
        const isOrganizationAsset = (point: GPSData) => {
          const unitName = typeof point.unit_name === 'string' ? point.unit_name.trim() : '';
          if (!point.unit_id || !unitName) {
            console.warn('Filtered out GPS point that is not an organization asset', point);
            return false;
          }

          const { latitude, longitude } = point;
          if (typeof latitude !== 'number' || typeof longitude !== 'number' || !Number.isFinite(latitude) || !Number.isFinite(longitude)) {
            console.warn(`Filtered out ${unitName} with missing coordinates`);
            return false;
          }
          if (latitude === 0 || longitude === 0) {
            console.warn(`Filtered out ${unitName} with zero coordinates: lat=${latitude}, lng=${longitude}`);
            return false;
          }
          if (latitude < -90 || latitude > 90) {
            console.warn(`Filtered out ${unitName} with invalid latitude: ${latitude}`);
            return false;
          }
          if (longitude < -180 || longitude > 180) {
            console.warn(`Filtered out ${unitName} with invalid longitude: ${longitude}`);
            return false;
          }
          return true;
        };

        // Process chargers
        if (response.data.chargers && Array.isArray(response.data.chargers)) {
          console.log('Charger GPS data received:', response.data.chargers);
          const validChargerGpsData = response.data.chargers
            .filter(isOrganizationAsset)
            .map(point => ({ ...point, assetType: 'charger' as const }));
          allGpsData.push(...validChargerGpsData);
          console.log('Valid charger GPS data after filtering:', validChargerGpsData);
        }
        
        // Process machines (note: API returns "machine" not "machines")
        if (response.data.machine && Array.isArray(response.data.machine)) {
          console.log('Machine GPS data received:', response.data.machine);
          const validMachineGpsData = response.data.machine
            .filter(isOrganizationAsset)
            .map(point => ({ ...point, assetType: 'machine' as const }));
          allGpsData.push(...validMachineGpsData);
          console.log('Valid machine GPS data after filtering:', validMachineGpsData);
        }
      } else {
        console.error('Error fetching organization GPS:', response);
      }
      
      console.log('All GPS data (chargers + machines):', allGpsData);
      setGpsData(allGpsData);
      gpsDataFetched.current = true;
    } catch (error) {
      console.error('GPS fetch error:', error);
      setGpsError(error instanceof Error ? error.message : 'Unknown error occurred');
      setGpsData([]);
      gpsDataFetched.current = true; // Mark as fetched even on error to prevent retry loops
    } finally {
      setLoadingGPS(false);
    }
  }, [orgId]);

  // Helper function to get time ago
  const getTimeAgo = useCallback((timestamp: string) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInHours = Math.floor((now.getTime() - time.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'less than 1 hour ago';
    if (diffInHours < 24) return `about ${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    return `about ${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
  }, []);

  // Convert GPS data to GeoJSON format
  // Groups points at the same location (rounded to 6 decimal places) to ensure clustering
  const gpsDataToGeoJSON = useCallback((data: (GPSData & { assetType?: 'charger' | 'machine' })[]) => {
    // Round coordinates to 6 decimal places (~0.1m precision) to group nearby points
    const roundCoordinate = (coord: number) => Math.round(coord * 1000000) / 1000000;
    
    // Group points by rounded coordinates using a Record
    const locationMap: Record<string, (GPSData & { assetType?: 'charger' | 'machine' })[]> = {};
    
    data.forEach((location) => {
      const roundedLat = roundCoordinate(location.latitude);
      const roundedLng = roundCoordinate(location.longitude);
      const key = `${roundedLat},${roundedLng}`;
      
      if (!locationMap[key]) {
        locationMap[key] = [];
      }
      locationMap[key].push(location);
    });
    
    // Create features - use the rounded coordinates for each group
    // Mapbox clustering will handle grouping points at the same location
    const features: GeoJSON.Feature[] = [];
    Object.entries(locationMap).forEach(([key, locations]) => {
      const [latStr, lngStr] = key.split(',');
      const lat = parseFloat(latStr);
      const lng = parseFloat(lngStr);
      
      // Create a feature for each location, using the rounded coordinates
      // This ensures points at the same location will cluster
      locations.forEach((location) => {
        const unitName = location.unit_name?.trim();
        if (!location.unit_id || !unitName) return;

        features.push({
          type: 'Feature' as const,
          geometry: {
            type: 'Point' as const,
            coordinates: [lng, lat],
          },
          properties: {
            unit_id: location.unit_id,
            unit_name: unitName,
            timestamp: location.timestamp,
            isCharger: location.assetType === 'charger',
            isMachine: location.assetType === 'machine',
            assetType: location.assetType || 'charger',
          },
        });
      });
    });
    
    return {
      type: 'FeatureCollection' as const,
      features,
    };
  }, []);

  // Function to create popup HTML
  const createPopupHTML = useCallback((location: GPSData & { assetType?: 'charger' | 'machine' }) => {
    const unitName = location.unit_name?.trim() || '';
    const unitId = location.unit_id;
    const assetType = location.assetType || 'charger';
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const isCharger = assetType === 'charger';
    const isMachine = assetType === 'machine';

    return `
      <div style="
        box-sizing: border-box;
        min-width: 240px;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      ">
        <!-- Top Section: Asset Name and GO TO button -->
        <div style="
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin: 0;
          width: 100%;
        ">
          <div style="display: flex; align-items: center; gap: 8px;">
            ${isMachine ? `
              <img 
                src="${process.env.PUBLIC_URL}/DiggerIcon.svg" 
                alt="Machine" 
                style="
                  width: 32px;
                  height: 32px;
                  flex-shrink: 0;
                  filter: brightness(0) saturate(100%) invert(42%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(100%) contrast(100%);
                "
              />
            ` : `
              <img 
                src="${process.env.PUBLIC_URL}/ev_station.svg" 
                alt="EV Station" 
                style="
                  width: 32px;
                  height: 32px;
                  flex-shrink: 0;
                  filter: brightness(0) saturate(100%) invert(42%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(100%) contrast(100%);
                "
              />
            `}
            <span style="font-size: 16px; font-weight: 600; color: #666666;">
              ${unitName}
            </span>
          </div>
    
          ${unitId ? `
             <div
               style="
                 display: flex;
                 align-items: center;
                 gap: 4px;
                 color: #4F785C;
                 cursor: pointer;
                 font-size: 14px;
                 font-weight: 400;
                 flex-shrink: 0;
                 margin: 4px;
                 padding: 4px 4px;
                 border-radius: 4px;
                 transition: background-color 0.2s ease;
               "
               onmouseover="this.style.backgroundColor='#4F785C20'"
               onmouseout="this.style.backgroundColor='transparent'"
               onclick="${isMachine ? `window.handleMachineClick('${unitId}')` : `window.handleChargerClick('${unitId}')`}"
             >
              <span>GO TO</span>
              <img
                src="${process.env.PUBLIC_URL}/arrow_forward.svg"
                alt="Go to"
                style="
                  width: 20px;
                  height: 20px;
                  filter: brightness(0) saturate(100%) invert(27%) sepia(15%) saturate(1200%) hue-rotate(90deg) brightness(90%) contrast(85%);
                "
              />
            </div>
          ` : ''}
        </div>
        
        <!-- Divider -->
        <div style="
          width: 100%;
          height: 1px;
          background-color: #B5AFA9;
          margin: 12px 0;
        "></div>
        
        <!-- Bottom Section: GPS Timestamp -->
        <div style="
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background-color: #0035A2;
          color: white;
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 12px;
          font-family: 'Montserrat', sans-serif;
          height: 24px;
          line-height: 16px;
          letter-spacing: 0.16px;
          margin-top: 0px;
        ">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="currentColor"
            style="color: white;"
          >
            <path d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4M12,6A6,6 0 0,0 6,12A6,6 0 0,0 12,18A6,6 0 0,0 18,12A6,6 0 0,0 12,6M12,8A4,4 0 0,1 16,12A4,4 0 0,1 12,16A4,4 0 0,1 8,12A4,4 0 0,1 12,8Z"/>
          </svg>
          <span style="color: white; font-size: 12px; line-height: 16px; letter-spacing: 0.16px;">
            ${getTimeAgo(location.timestamp)} (GPS)
          </span>
        </div>
      </div>
    `;
  }, [getTimeAgo]);

  // Function to add markers to the map using clustering
  const addMarkersToMap = useCallback(() => {
    if (!map.current || !gpsData || gpsData.length === 0) return;

    // Expose navigation functions to global scope for popup onclick
    (window as any).handleChargerClick = handleChargerNavigation;
    (window as any).handleMachineClick = handleMachineNavigation;

    // Convert GPS data to GeoJSON
    const geoJsonData = gpsDataToGeoJSON(gpsData as (GPSData & { assetType?: 'charger' | 'machine' })[]);

    // Remove existing event listeners
    if (eventHandlers.current.clusterClick) {
      map.current.off('click', clusterLayerId, eventHandlers.current.clusterClick);
    }
    if (eventHandlers.current.clusterMouseEnter) {
      map.current.off('mouseenter', clusterLayerId, eventHandlers.current.clusterMouseEnter);
    }
    if (eventHandlers.current.clusterMouseLeave) {
      map.current.off('mouseleave', clusterLayerId, eventHandlers.current.clusterMouseLeave);
    }
    if (eventHandlers.current.mapMoveEnd) {
      map.current.off('moveend', eventHandlers.current.mapMoveEnd);
      map.current.off('zoomend', eventHandlers.current.mapMoveEnd);
    }
    eventHandlers.current = {};

    // Clear existing custom markers
    markers.current.forEach(marker => marker.remove());
    markers.current = [];

    // Remove existing source and layers if they exist
    if (map.current.getSource(sourceId)) {
      if (map.current.getLayer(clusterLayerId)) map.current.removeLayer(clusterLayerId);
      if (map.current.getLayer(clusterCountLayerId)) map.current.removeLayer(clusterCountLayerId);
      map.current.removeSource(sourceId);
    }

    // Add GeoJSON source with clustering
    map.current.addSource(sourceId, {
      type: 'geojson',
      data: geoJsonData,
      cluster: true,
      clusterMaxZoom: 14, // Max zoom to cluster points on
      clusterRadius: 20, // Radius of each cluster when clustering points (smaller to group same locations)
    });

    // Add cluster circles layer
    map.current.addLayer({
      id: clusterLayerId,
      type: 'circle',
      source: sourceId,
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': theme.palette.secondary.dark,
        'circle-radius': 20, // Always 20 pixels, regardless of point count
        'circle-stroke-width': 0, // No border on cluster circles
        'circle-stroke-color': 'transparent',
      },
    });

    // Add cluster count labels
    map.current.addLayer({
      id: clusterCountLayerId,
      type: 'symbol',
      source: sourceId,
      filter: ['has', 'point_count'],
      layout: {
        'text-field': '{point_count_abbreviated}',
        'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
        'text-size': 14,
      },
      paint: {
        'text-color': '#fff',
      },
    });

    // Function to update custom markers for unclustered points
    // This will be called after source loads and when map moves/zooms
    const updateUnclusteredMarkers = () => {
      if (!map.current || !map.current.getSource(sourceId)) return;

      // Clear existing markers
      markers.current.forEach(marker => marker.remove());
      markers.current = [];

      // Ensure the map has finished rendering at least once
      if (!map.current.isStyleLoaded()) {
        return;
      }

      const features = map.current.querySourceFeatures(sourceId, {
        filter: ['!', ['has', 'point_count']],
      });

      const uniqueFeatureKeys = new Set<string>();

      features.forEach((feature, index) => {
        if (feature.geometry.type !== 'Point') return;

        const coordinates = feature.geometry.coordinates as [number, number];
        const properties = feature.properties || {};
        const key = `${coordinates[0]}-${coordinates[1]}-${properties.unit_id || index}`;

        if (uniqueFeatureKeys.has(key)) return;
        uniqueFeatureKeys.add(key);

        const unitName = typeof properties.unit_name === 'string' ? properties.unit_name.trim() : '';
        if (!properties.unit_id || !unitName) return;

        const assetType = (properties.assetType as 'charger' | 'machine') || 'charger';
        const isMachine = assetType === 'machine';

        const location: GPSData & { assetType?: 'charger' | 'machine' } = {
          unit_id: properties.unit_id || undefined,
          unit_name: unitName,
          timestamp: properties.timestamp,
          latitude: coordinates[1],
          longitude: coordinates[0],
          assetType,
        };

        // Create custom marker icon element using React
        const el = document.createElement('div');
        const root = createRoot(el);
        root.render(isMachine ? <MachineMarker /> : <EvStationMarker />);

        const marker = new mapboxgl.Marker(el).setLngLat(coordinates).addTo(map.current!);

        // Add tooltip on hover
        const tooltip = new mapboxgl.Popup({
          closeButton: false,
          closeOnClick: false,
          offset: [0, -25],
          className: 'marker-tooltip',
        }).setText(unitName);

        // Add custom styles for the tooltip (only once)
        if (!document.getElementById('marker-tooltip-style')) {
          const style = document.createElement('style');
          style.id = 'marker-tooltip-style';
          style.textContent = `
            .marker-tooltip .mapboxgl-popup-content {
              background-color: #413C38 !important;
              color: white !important;
              padding: 8px 12px !important;
              border-radius: 4px !important;
              font-size: 12px !important;
              font-family: 'Montserrat', sans-serif !important;
              border: none !important;
              margin: 0 !important;
            }
            .marker-tooltip .mapboxgl-popup-tip {
              border-top-color: #413C38 !important;
              border-bottom-color: transparent !important;
              border-left-color: transparent !important;
              border-right-color: transparent !important;
              box-shadow: none !important;
            }
            .marker-tooltip {
              border: none !important;
              box-shadow: none !important;
            }
          `;
          document.head.appendChild(style);
        }

        el.addEventListener('mouseenter', () => {
          tooltip.setLngLat(coordinates).addTo(map.current!);
        });

        el.addEventListener('mouseleave', () => {
          tooltip.remove();
        });

        const popup = new mapboxgl.Popup({
          offset: 25,
          closeButton: false,
          closeOnClick: true,
          maxWidth: '320px',
        }).setHTML(createPopupHTML(location));

        marker.setPopup(popup);
        markers.current.push(marker);
      });
    };

    // Update markers after source is loaded and map is rendered
    // Use 'idle' event to ensure clusters are fully rendered before querying
    const updateMarkersWhenReady = () => {
      if (map.current && map.current.isStyleLoaded() && map.current.loaded()) {
        // Small delay to ensure clusters are rendered
        setTimeout(updateUnclusteredMarkers, 150);
      }
    };

    // Try to update when source data is loaded
    map.current.once('sourcedata', updateMarkersWhenReady);
    
    // Also update when map becomes idle (fully rendered)
    if (map.current.loaded()) {
      map.current.once('idle', updateMarkersWhenReady);
    } else {
      map.current.once('load', () => {
        map.current!.once('idle', updateMarkersWhenReady);
      });
    }

    // Handle click on clusters - zoom in
    eventHandlers.current.clusterClick = (e) => {
      const features = map.current!.queryRenderedFeatures(e.point, {
        layers: [clusterLayerId],
      });
      
      if (!features || features.length === 0) return;
      
      const clusterId = features[0].properties?.cluster_id;
      
      if (clusterId !== undefined && clusterId !== null) {
        // Get the cluster center coordinates from the feature geometry
        // GeoJSON Point coordinates are [longitude, latitude], which is what Mapbox expects
        const featureGeometry = features[0].geometry as GeoJSON.Point;
        const clusterCenter: [number, number] = [
          featureGeometry.coordinates[0], // longitude
          featureGeometry.coordinates[1]  // latitude
        ];
        
        (map.current!.getSource(sourceId) as mapboxgl.GeoJSONSource).getClusterExpansionZoom(
          clusterId,
          (err, zoom) => {
            if (err || zoom === null || zoom === undefined) return;

            // Zoom in further by adding an increment to the expansion zoom
            // This ensures we zoom past the point where the cluster breaks apart
            const targetZoom = Math.min(zoom + 2, 18); // Add 2 zoom levels, but cap at 18

            // Use flyTo for smooth animation that zooms directly to the cluster center
            // flyTo is designed for smooth camera movements and handles center + zoom together
            map.current!.flyTo({
              center: clusterCenter,
              zoom: targetZoom,
              duration: 1000, // 1 second smooth animation
              essential: true, // This animation is essential and cannot be interrupted
            });
          }
        );
      }
    };
    map.current.on('click', clusterLayerId, eventHandlers.current.clusterClick);

    // Change cursor on hover for clusters
    eventHandlers.current.clusterMouseEnter = () => {
      map.current!.getCanvas().style.cursor = 'pointer';
    };
    map.current.on('mouseenter', clusterLayerId, eventHandlers.current.clusterMouseEnter);

    eventHandlers.current.clusterMouseLeave = () => {
      map.current!.getCanvas().style.cursor = '';
    };
    map.current.on('mouseleave', clusterLayerId, eventHandlers.current.clusterMouseLeave);

    // Update markers when map moves/zooms (clustering changes)
    // Use 'idle' event to ensure clusters are fully rendered before querying
    eventHandlers.current.mapMoveEnd = () => {
      // Wait for map to be idle (fully rendered) before updating markers
      if (map.current) {
        map.current.once('idle', () => {
          updateUnclusteredMarkers();
        });
      }
    };
    map.current.on('moveend', eventHandlers.current.mapMoveEnd);
    map.current.on('zoomend', eventHandlers.current.mapMoveEnd);

    // Render markers immediately without waiting for user interaction
    if (map.current && map.current.isStyleLoaded()) {
      updateUnclusteredMarkers();
    } else if (map.current) {
      map.current.once('load', () => {
        updateUnclusteredMarkers();
      });
    }
  }, [gpsData, handleChargerNavigation, handleMachineNavigation, gpsDataToGeoJSON, createPopupHTML]);

  // Preload both icon images on component mount (same setup for both)
  useEffect(() => {
    if (iconsPreloaded.current) return;

    const chargerIcon = new Image();
    chargerIcon.src = process.env.PUBLIC_URL + "/ev_station.svg";
    
    const machineIcon = new Image();
    machineIcon.src = process.env.PUBLIC_URL + "/DiggerIcon.svg";
    
    let chargerLoaded = false;
    let machineLoaded = false;
    
    const checkBothLoaded = () => {
      if (chargerLoaded && machineLoaded) {
        iconsPreloaded.current = true;
      }
    };
    
    chargerIcon.onload = () => {
      chargerLoaded = true;
      checkBothLoaded();
    };
    chargerIcon.onerror = () => {
      chargerLoaded = true; // Mark as attempted
      checkBothLoaded();
    };
    
    machineIcon.onload = () => {
      machineLoaded = true;
      checkBothLoaded();
    };
    machineIcon.onerror = () => {
      machineLoaded = true; // Mark as attempted
      checkBothLoaded();
    };
  }, []);

  // Fetch GPS data on component mount
  useEffect(() => {
    if (orgId) {
      // Reset fetch flags when orgId changes
      gpsDataFetched.current = false;
      setGpsData([]);
      fetchGPSData();
    }
  }, [orgId, fetchGPSData]);

  useEffect(() => {
    if (map.current) return; // initialize map only once
    if (!mapContainer.current) return;
    if (!hasValidToken) {
      setMapError('Mapbox access token is missing or invalid. Please configure REACT_APP_MAPBOX_ACCESS_TOKEN in your environment variables.');
      return;
    }

    // Function to initialize the map
    const initializeMap = () => {
      try {
        console.log('Initializing map...');
        const container = mapContainer.current!;
        console.log('Container dimensions:', container.offsetWidth, 'x', container.offsetHeight);
        
        // Ensure container has dimensions
        if (container.offsetWidth === 0 || container.offsetHeight === 0) {
          console.log('Container has no dimensions, retrying...');
          setTimeout(initializeMap, 500);
          return;
        }

        map.current = new mapboxgl.Map({
          container: container,
          style: 'mapbox://styles/mapbox/streets-v11',
          center: [lng, lat],
          zoom: zoom
        });

        map.current.on('load', () => {
          console.log('Map loaded successfully');
          setMapError(null); // Clear any previous errors
        });

        map.current.on('error', (e) => {
          console.error('Map error:', e);
          if (e.error && e.error.message) {
            setMapError(`Map error: ${e.error.message}`);
          } else {
            setMapError('An error occurred while loading the map');
          }
        });

        // Don't update state on move to prevent re-initialization
        // The map will maintain its own state internally

        // Navigation controls removed - using custom controls instead
        
        console.log('Map initialization complete');
      } catch (error) {
        console.error('Error initializing map:', error);
        setMapError('Failed to initialize map. Please check your Mapbox configuration.');
      }
    };

    // Start initialization with a delay to ensure DOM is ready
    const timer = setTimeout(initializeMap, 100);

    return () => {
      clearTimeout(timer);
      if (map.current) {
        // Remove event listeners
        if (eventHandlers.current.clusterClick) {
          map.current.off('click', clusterLayerId, eventHandlers.current.clusterClick);
        }
        if (eventHandlers.current.clusterMouseEnter) {
          map.current.off('mouseenter', clusterLayerId, eventHandlers.current.clusterMouseEnter);
        }
        if (eventHandlers.current.clusterMouseLeave) {
          map.current.off('mouseleave', clusterLayerId, eventHandlers.current.clusterMouseLeave);
        }
        if (eventHandlers.current.mapMoveEnd) {
          map.current.off('moveend', eventHandlers.current.mapMoveEnd);
          map.current.off('zoomend', eventHandlers.current.mapMoveEnd);
        }
        
        // Clear custom markers
        markers.current.forEach(marker => marker.remove());
        markers.current = [];
        
        // Remove layers and source
        if (map.current.getLayer(clusterLayerId)) map.current.removeLayer(clusterLayerId);
        if (map.current.getLayer(clusterCountLayerId)) map.current.removeLayer(clusterCountLayerId);
        if (map.current.getSource(sourceId)) map.current.removeSource(sourceId);
        
        map.current.remove();
        map.current = null;
      }
      // Clean up global functions
      delete (window as any).handleChargerClick;
      delete (window as any).handleMachineClick;
    };
  }, [hasValidToken, lng, lat, zoom]); // Only depend on hasValidToken to prevent re-initialization

  // Effect to add markers when GPS data changes
  useEffect(() => {
    if (map.current && map.current.isStyleLoaded() && gpsData && gpsData.length > 0) {
      addMarkersToMap();
    } else if (map.current && gpsData && gpsData.length > 0) {
      // Wait for map to load if it's not ready yet
      map.current.once('load', () => {
        addMarkersToMap();
      });
    }
  }, [addMarkersToMap, gpsData]);

  // Effect to resize map when sidebar collapses/expands or window resizes
  useEffect(() => {
    if (map.current) {
      // Small delay to allow the transition to complete
      const resizeTimer = setTimeout(() => {
        map.current?.resize();
      }, 300); // Match the transition duration
      
      return () => clearTimeout(resizeTimer);
    }
  }, [sidebarCollapsed]);

  // Handle window resize to ensure map fills available space
  useEffect(() => {
    const handleResize = () => {
      if (map.current) {
        // Delay resize slightly to ensure container has updated dimensions
        setTimeout(() => {
          map.current?.resize();
        }, 100);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleMapControl = (action: string) => {
    if (!map.current) return;

    switch (action) {
      case 'zoomIn':
        map.current.zoomIn();
        break;
      case 'zoomOut':
        map.current.zoomOut();
        break;
      case 'location':
        // Get user's current location
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              const { longitude, latitude } = position.coords;
              map.current!.flyTo({
                center: [longitude, latitude],
                zoom: 14
              });
            },
            (error) => {
              console.error('Error getting location:', error);
            }
          );
        }
        break;
      case 'fullscreen':
        // Toggle fullscreen
        if (mapContainer.current) {
          if (document.fullscreenElement) {
            document.exitFullscreen();
          } else {
            mapContainer.current.requestFullscreen();
          }
        }
        break;
    }
  };

  return (
    <Box sx={{ 
      position: 'fixed',
      top: '64px', // Account for TopBar height
      left: { 
        xs: 0, 
        md: `${sidebarWidth}px` // Account for sidebar width (responds to collapse)
      },
      right: 0, // Ensure it fills to the right edge
      bottom: 0,
      width: { 
        xs: '100vw',
        md: `calc(100vw - ${sidebarWidth}px)` // Full width minus sidebar - extends to right edge
      },
      maxWidth: 'none', // Remove any max width constraints
      height: 'calc(100vh - 64px)', // Full height minus topbar
      zIndex: 1,
      backgroundColor: (theme) => theme.palette.background.default,
      transition: (theme) => theme.transitions.create(['left', 'width'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
      overflow: 'hidden', // Prevent any scrolling
    }}>
      <div 
        ref={mapContainer} 
        style={{ 
          width: '100%', 
          height: '100%',
          minHeight: '400px', // Ensure minimum height
          position: 'relative',
          maxWidth: 'none', // Ensure no max width constraint
        }} 
      />

      {/* Error Display */}
      {mapError && (
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            left: 16,
            right: 16,
            zIndex: 10,
          }}
        >
          <Alert 
            severity={hasValidToken ? "error" : "warning"}
            onClose={() => setMapError(null)}
            sx={{ maxWidth: '600px' }}
          >
            {mapError}
            {!hasValidToken && (
              <Box sx={{ mt: 1, fontSize: '0.875rem' }}>
                <strong>To fix this:</strong>
                <ol style={{ margin: '8px 0', paddingLeft: '20px' }}>
                  <li>Go to <a href="https://account.mapbox.com/access-tokens/" target="_blank" rel="noopener noreferrer">Mapbox Account</a></li>
                  <li>Create a new access token or copy your existing one</li>
                  <li>Create a <code>.env</code> file in your project root</li>
                  <li>Add: <code>REACT_APP_MAPBOX_ACCESS_TOKEN=pk.your_token_here</code></li>
                  <li>Restart the development server</li>
                </ol>
              </Box>
            )}
          </Alert>
        </Box>
      )}

      {/* GPS Error Display */}
      {gpsError && (
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            left: 16,
            right: 16,
            zIndex: 10,
          }}
        >
          <Alert 
            severity="error"
            onClose={() => setGpsError(null)}
            sx={{ maxWidth: '600px' }}
          >
            GPS Position Unavailable: {gpsError}
          </Alert>
        </Box>
      )}

      {/* No GPS Data Available */}
      {!loadingGPS && !gpsError && gpsData.length === 0 && (
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            left: 16,
            right: 16,
            zIndex: 10,
          }}
        >
          <Alert 
            severity="warning"
            sx={{ maxWidth: '600px' }}
          >
            No GPS position data available for this unit
          </Alert>
        </Box>
      )}

      {/* GPS Loading Indicator */}
      {loadingGPS && (
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            left: 16,
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            bgcolor: 'white',
            padding: 1,
            borderRadius: 1,
            boxShadow: 2,
          }}
        >
          <CircularProgress size={20} />
          <span style={{ fontSize: '14px' }}>Loading unit positions...</span>
        </Box>
      )}


      {/* Custom Map Controls */}
      <Box
        sx={{
          position: 'absolute',
          top: 16,
          right: 80, // Position to the left of Mapbox's default controls
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        {mapControls.map((control, index) => (
          <IconButton
            key={index}
            onClick={() => handleMapControl(control.action)}
            sx={{
              bgcolor: 'white',
              boxShadow: 2,
              '&:hover': { bgcolor: 'grey.50' },
            }}
          >
            {control.icon}
          </IconButton>
        ))}
      </Box>
    </Box>
  );
};

export default Map;
